import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useChefsLab } from '../../context/ChefsLabContext';

// Close icon
const CloseIcon = ({ color }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

// Check icon
const CheckIcon = ({ color }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const REPORT_REASONS = [
  { id: 'variant', label: 'Wrong Variant', description: 'First edition, unlimited, shadowless mismatch' },
  { id: 'grade', label: 'Wrong Grade', description: 'Grade number doesn\'t match' },
  { id: 'company', label: 'Wrong Company', description: 'PSA, CGC, BGS mismatch' },
  { id: 'card', label: 'Wrong Card', description: 'Different Pokemon or set' },
  { id: 'fake', label: 'Fake/Replica', description: 'Not an authentic card' },
  { id: 'other', label: 'Other', description: 'Something else is wrong' },
];

const ACTION_OPTIONS = [
  { id: 'display', label: 'Remove from display only', description: 'Hide from view but keep in calculations' },
  { id: 'cost', label: 'Remove from calculations only', description: 'Show but exclude from price averages' },
  { id: 'both', label: 'Remove from both', description: 'Hide and exclude from calculations' },
];

const ReportModal = ({ isOpen, onClose, listing }) => {
  const { theme } = useTheme();
  const { addReportedListing } = useChefsLab();

  const [step, setStep] = useState(1);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [notes, setNotes] = useState('');
  const [selectedAction, setSelectedAction] = useState('both');

  const handleReasonToggle = (reasonId) => {
    setSelectedReasons(prev =>
      prev.includes(reasonId)
        ? prev.filter(r => r !== reasonId)
        : [...prev, reasonId]
    );
  };

  const handleSubmit = () => {
    if (!listing?.url) return;

    addReportedListing(listing.url, {
      reasons: selectedReasons,
      notes: notes.trim(),
      action: selectedAction,
      cardIdentifier: `${listing.title?.substring(0, 50)}`,
    });

    // Reset and close
    setStep(1);
    setSelectedReasons([]);
    setNotes('');
    setSelectedAction('both');
    onClose();
  };

  const handleClose = () => {
    setStep(1);
    setSelectedReasons([]);
    setNotes('');
    setSelectedAction('both');
    onClose();
  };

  const styles = {
    backdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: theme.overlay,
      zIndex: 1000,
    },
    modal: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: theme.bgSheet,
      borderTopLeftRadius: '20px',
      borderTopRightRadius: '20px',
      maxHeight: '70vh',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1001,
    },
    handle: {
      width: '40px',
      height: '4px',
      backgroundColor: theme.border,
      borderRadius: '2px',
      margin: '12px auto',
      flexShrink: 0,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 20px 16px',
      borderBottom: `1px solid ${theme.border}`,
      flexShrink: 0,
    },
    title: {
      color: theme.text,
      fontSize: '18px',
      fontWeight: 600,
      margin: 0,
    },
    stepIndicator: {
      color: theme.textMuted,
      fontSize: '13px',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      padding: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    content: {
      padding: '20px',
      overflowY: 'auto',
      flex: 1,
      minHeight: 0,
    },
    listingPreview: {
      backgroundColor: theme.bgCard,
      borderRadius: '10px',
      padding: '12px',
      marginBottom: '20px',
    },
    listingTitle: {
      color: theme.text,
      fontSize: '13px',
      margin: 0,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    listingMeta: {
      color: theme.textMuted,
      fontSize: '12px',
      marginTop: '4px',
    },
    sectionTitle: {
      color: theme.textMuted,
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      margin: '0 0 12px 0',
    },
    reasonItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '12px',
      backgroundColor: theme.bgCard,
      borderRadius: '10px',
      marginBottom: '8px',
      cursor: 'pointer',
      border: `1px solid ${theme.border}`,
    },
    reasonItemSelected: {
      borderColor: theme.accent,
      backgroundColor: theme.accentBg,
    },
    checkbox: {
      width: '24px',
      height: '24px',
      borderRadius: '6px',
      border: `2px solid ${theme.border}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    checkboxSelected: {
      backgroundColor: theme.accent,
      borderColor: theme.accent,
    },
    reasonContent: {
      flex: 1,
    },
    reasonLabel: {
      color: theme.text,
      fontSize: '14px',
      fontWeight: 500,
      margin: 0,
    },
    reasonDescription: {
      color: theme.textMuted,
      fontSize: '12px',
      margin: '2px 0 0 0',
    },
    textarea: {
      width: '100%',
      minHeight: '80px',
      padding: '12px',
      backgroundColor: theme.bgInput,
      border: `1px solid ${theme.border}`,
      borderRadius: '10px',
      color: theme.text,
      fontSize: '14px',
      resize: 'vertical',
      marginTop: '12px',
    },
    radioItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '12px',
      backgroundColor: theme.bgCard,
      borderRadius: '10px',
      marginBottom: '8px',
      cursor: 'pointer',
      border: `1px solid ${theme.border}`,
    },
    radioItemSelected: {
      borderColor: theme.accent,
      backgroundColor: theme.accentBg,
    },
    radio: {
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      border: `2px solid ${theme.border}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    radioSelected: {
      borderColor: theme.accent,
    },
    radioDot: {
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      backgroundColor: theme.accent,
    },
    footer: {
      display: 'flex',
      gap: '12px',
      padding: '16px 20px',
      paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
      borderTop: `1px solid ${theme.border}`,
      flexShrink: 0,
    },
    button: {
      flex: 1,
      padding: '14px',
      borderRadius: '10px',
      fontSize: '15px',
      fontWeight: 600,
      cursor: 'pointer',
    },
    secondaryButton: {
      backgroundColor: theme.bgInput,
      border: `1px solid ${theme.border}`,
      color: theme.text,
    },
    primaryButton: {
      backgroundColor: theme.accent,
      border: 'none',
      color: '#fff',
    },
    primaryButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        style={styles.backdrop}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      />
      <motion.div
        style={styles.modal}
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      >
        <div style={styles.handle} />

        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Report Listing</h2>
            <span style={styles.stepIndicator}>Step {step} of 2</span>
          </div>
          <button style={styles.closeButton} onClick={handleClose}>
            <CloseIcon color={theme.textMuted} />
          </button>
        </div>

        <div style={styles.content}>
          {/* Listing Preview */}
          {listing && (
            <div style={styles.listingPreview}>
              <p style={styles.listingTitle}>{listing.title || 'Untitled Listing'}</p>
              <p style={styles.listingMeta}>
                ${listing.price?.toFixed(2)} - {listing.date}
              </p>
            </div>
          )}

          {step === 1 && (
            <>
              <p style={styles.sectionTitle}>What's wrong with this listing?</p>

              {REPORT_REASONS.map(reason => {
                const isSelected = selectedReasons.includes(reason.id);
                return (
                  <div
                    key={reason.id}
                    style={{
                      ...styles.reasonItem,
                      ...(isSelected ? styles.reasonItemSelected : {}),
                    }}
                    onClick={() => handleReasonToggle(reason.id)}
                  >
                    <div style={{
                      ...styles.checkbox,
                      ...(isSelected ? styles.checkboxSelected : {}),
                    }}>
                      {isSelected && <CheckIcon color="#fff" />}
                    </div>
                    <div style={styles.reasonContent}>
                      <p style={styles.reasonLabel}>{reason.label}</p>
                      <p style={styles.reasonDescription}>{reason.description}</p>
                    </div>
                  </div>
                );
              })}

              <textarea
                style={styles.textarea}
                placeholder="Additional notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </>
          )}

          {step === 2 && (
            <>
              <p style={styles.sectionTitle}>Choose an action</p>

              {ACTION_OPTIONS.map(option => {
                const isSelected = selectedAction === option.id;
                return (
                  <div
                    key={option.id}
                    style={{
                      ...styles.radioItem,
                      ...(isSelected ? styles.radioItemSelected : {}),
                    }}
                    onClick={() => setSelectedAction(option.id)}
                  >
                    <div style={{
                      ...styles.radio,
                      ...(isSelected ? styles.radioSelected : {}),
                    }}>
                      {isSelected && <div style={styles.radioDot} />}
                    </div>
                    <div style={styles.reasonContent}>
                      <p style={styles.reasonLabel}>{option.label}</p>
                      <p style={styles.reasonDescription}>{option.description}</p>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div style={styles.footer}>
          {step === 1 ? (
            <>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                style={{
                  ...styles.button,
                  ...styles.primaryButton,
                  ...(selectedReasons.length === 0 ? styles.primaryButtonDisabled : {}),
                }}
                onClick={() => selectedReasons.length > 0 && setStep(2)}
                disabled={selectedReasons.length === 0}
              >
                Next
              </button>
            </>
          ) : (
            <>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={() => setStep(1)}
              >
                Back
              </button>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={handleSubmit}
              >
                Submit Report
              </button>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ReportModal;
